import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './AssociationPromotionRulesPage.css';
import {
  ActionKey,
  type AssociationPromotionRules,
  type AssociationPromotionRule,
  type PublishAssociationPromotionRule,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore, sessionVenueKey } from '../../store/auth.store';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';

const endpoint = '/members/qualification/rules';
const taipeiInput = (iso: string) =>
  new Date(Date.parse(iso) + 8 * 3600000).toISOString().slice(0, 16);
type Draft = Omit<PublishAssociationPromotionRule, 'effectiveFrom'> & { effectiveLocal: string };

export default function AssociationPromotionRulesPage() {
  const session = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((state) => sessionVenueKey(state.accessToken));
  return <PromotionRules key={`${session}:${venue}`} session={session} venue={venue} />;
}

function PromotionRules({ session, venue }: { session: number; venue: string }) {
  const { t, i18n } = useTranslation('associationPromotion');
  const actions = useAuthStore((s) => s.allowedActions);
  const canRead = actions?.includes(ActionKey.MEMBER_READ) ?? false;
  const canWrite = actions?.includes(ActionKey.MEMBER_LEVEL_MANAGE) ?? false;
  const queryKey = ['association-promotion-rules', session, venue];
  const query = useQuery({
    queryKey,
    enabled: canRead,
    retry: false,
    queryFn: () => api.get<AssociationPromotionRules>(endpoint),
  });
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const busy = useRef(false);
  const save = useMutation({
    retry: false,
    mutationFn: async (input: PublishAssociationPromotionRule) => {
      const published = await api.post<AssociationPromotionRule>(endpoint, input);
      if (published?.revision !== input.expectedRevision + 1)
        throw new Error('Unconfirmed publication');
      const confirmed = await api.get<AssociationPromotionRules>(endpoint);
      if (
        !confirmed.revisions.some(
          (rule) =>
            rule.revision === published.revision &&
            rule.vipThreshold === published.vipThreshold &&
            rule.blackThreshold === published.blackThreshold &&
            rule.effectiveFrom === published.effectiveFrom &&
            rule.reason === published.reason,
        )
      )
        throw new Error('Publication read-back mismatch');
      return confirmed;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKey, data);
      setDraft(null);
      setMessage(t('saved'));
    },
    onError: (cause: unknown) => {
      const status = axios.isAxiosError(cause) ? cause.response?.status : undefined;
      setError(
        t(
          status === 409
            ? 'conflict'
            : status === 403
              ? 'denied'
              : status === 400
                ? 'invalid'
                : 'uncertain',
        ),
      );
    },
  });
  const data = query.data;
  const current: Draft | null =
    draft ??
    (data
      ? {
          expectedRevision: data.latest.revision,
          vipThreshold: data.latest.vipThreshold,
          blackThreshold: data.latest.blackThreshold,
          effectiveLocal: taipeiInput(
            new Date(
              Math.max(Date.parse(data.serverTime), Date.parse(data.latest.effectiveFrom)) +
                5 * 60000,
            ).toISOString(),
          ),
          reason: '',
        }
      : null);
  const change = (patch: Partial<Draft>) => {
    if (current) setDraft({ ...current, ...patch });
    setMessage('');
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!current || !canWrite || busy.current) return;
    const decimal = /^(0|[1-9]\d{0,9})(\.\d{1,2})?$/;
    const effective = new Date(`${current.effectiveLocal}:00+08:00`);
    if (
      !decimal.test(current.vipThreshold) ||
      !decimal.test(current.blackThreshold) ||
      Number(current.vipThreshold) <= 0 ||
      Number(current.blackThreshold) <= Number(current.vipThreshold) ||
      !current.reason.trim() ||
      !Number.isFinite(effective.getTime()) ||
      effective.getTime() <= Date.now()
    ) {
      setError(t('invalid'));
      return;
    }
    busy.current = true;
    setError('');
    setMessage('');
    try {
      await save.mutateAsync({
        expectedRevision: current.expectedRevision,
        vipThreshold: current.vipThreshold,
        blackThreshold: current.blackThreshold,
        effectiveFrom: effective.toISOString(),
        reason: current.reason.trim(),
      });
    } catch {
      /* Mutation error is shown above; preserve the draft. */
    } finally {
      busy.current = false;
    }
  };
  const reload = async () => {
    setMessage('');
    const refreshed = await query.refetch();
    if (refreshed.data && !refreshed.isError) {
      setDraft((value) =>
        value ? { ...value, expectedRevision: refreshed.data.latest.revision } : null,
      );
      setError('');
    }
  };
  const date = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, { timeZone: 'Asia/Taipei' });
  return (
    <AdminPage className="promotion-rules">
      <AdminPageHeader title={t('title')} description={t('description')} />
      {!canRead ? (
        <p role="alert">{t('denied')}</p>
      ) : (
        <div className="promotion-content">
          <button
            type="button"
            className="admin-button"
            disabled={save.isPending || query.isFetching}
            onClick={() => void reload()}
          >
            {t('reload')}
          </button>
          {query.isPending && <p role="status">{t('loading')}</p>}
          {query.isError && <p role="alert">{t('loadError')}</p>}
          {error && (
            <p role="alert" className="promotion-error">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="promotion-success">
              {message}
            </p>
          )}
          {data && current && (
            <>
              <section className="admin-panel promotion-section" aria-label={t('active')}>
                <h2 className="font-semibold">
                  {t('active')} · {t('revision', { revision: data.active.revision })}
                </h2>
                <p>
                  VIP: {data.active.vipThreshold} TIMED · {t('black')}: {data.active.blackThreshold}{' '}
                  TIMED
                </p>
                <p className="promotion-help">{t('monthlyClose')}</p>
                <p className="promotion-help">{t('protections')}</p>
              </section>
              {canWrite && (
                <form
                  onSubmit={(event) => void submit(event)}
                  className="admin-panel promotion-section"
                >
                  <h2 className="font-semibold">{t('publish')}</h2>
                  <p className="text-sm">
                    {t('editingRevision', { revision: current.expectedRevision })}
                  </p>
                  <fieldset disabled={save.isPending} className="promotion-form-grid">
                    <label className="promotion-label">
                      {t('vipThreshold')}
                      <input
                        id="promotion-vip"
                        className="promotion-input"
                        inputMode="decimal"
                        required
                        maxLength={13}
                        value={current.vipThreshold}
                        onChange={(e) => change({ vipThreshold: e.target.value })}
                      />
                    </label>
                    <label className="promotion-label">
                      {t('blackThreshold')}
                      <input
                        id="promotion-black"
                        className="promotion-input"
                        inputMode="decimal"
                        required
                        maxLength={13}
                        value={current.blackThreshold}
                        onChange={(e) => change({ blackThreshold: e.target.value })}
                      />
                    </label>
                    <label className="promotion-wide">
                      {t('effectiveFrom')}
                      <input
                        id="promotion-effective"
                        type="datetime-local"
                        className="promotion-input"
                        required
                        value={current.effectiveLocal}
                        onChange={(e) => change({ effectiveLocal: e.target.value })}
                      />
                      <span className="promotion-help">{t('timezone')}</span>
                    </label>
                    <label className="promotion-wide">
                      {t('reason')}
                      <textarea
                        id="promotion-reason"
                        className="promotion-input"
                        required
                        maxLength={500}
                        rows={3}
                        value={current.reason}
                        onChange={(e) => change({ reason: e.target.value })}
                      />
                    </label>
                  </fieldset>
                  <p className="promotion-help">{t('publishHint')}</p>
                  <button
                    type="submit"
                    className="admin-button admin-button-primary"
                    disabled={save.isPending || query.isError}
                  >
                    {save.isPending ? t('saving') : t('save')}
                  </button>
                </form>
              )}
              <section aria-label={t('history')}>
                <h2 className="promotion-history-title">{t('history')}</h2>
                <ol className="promotion-history">
                  {data.revisions.map((rule) => (
                    <li key={rule.revision} className="admin-panel">
                      <strong>{t('revision', { revision: rule.revision })}</strong> · VIP{' '}
                      {rule.vipThreshold} / {t('black')} {rule.blackThreshold} TIMED
                      <p>
                        {t('effectiveFrom')}: {date(rule.effectiveFrom)} (Asia/Taipei)
                      </p>
                      <p>{rule.reason}</p>
                    </li>
                  ))}
                </ol>
              </section>
            </>
          )}
        </div>
      )}
    </AdminPage>
  );
}
